import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { Progress } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  subtitle: { color: theme.palette.text.hint, fontSize: '0.75rem' },
  viewAll: { display: 'block', marginTop: theme.spacing(1), textAlign: 'right' },
}));

export const MyAppsWidget = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const { value: apps, loading, error } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const ownerRef = identity.userEntityRef;
    const response = await catalogApi.getEntities({
      filter: { kind: 'Resource', 'spec.owner': ownerRef },
      fields: ['metadata.name', 'metadata.namespace', 'metadata.annotations', 'spec.owner'],
    });
    return response.items as Entity[];
  }, []);

  if (loading) return <Progress />;
  if (error) {
    return (
      <Typography color="error" variant="body2">
        Failed to load apps: {error.message}
      </Typography>
    );
  }

  return (
    <Card>
      <CardHeader title="My Apps" />
      <CardContent>
        {!apps?.length ? (
          <Typography variant="body2" color="textSecondary">
            No apps yet.{' '}
            <Link href="/create">Create your first starter app →</Link>
          </Typography>
        ) : (
          <List dense>
            {apps.map(app => {
              const ns = app.metadata.namespace ?? 'default';
              const a = app.metadata.annotations ?? {};
              const frontend = a['arkance.world/frontend'] ?? '';
              const backend = a['arkance.world/backend'] ?? '';
              const createdAt = a['arkance.world/created-at'];
              const stack = [frontend, backend].filter(Boolean).join(' + ');
              const date = createdAt
                ? new Date(createdAt).toLocaleDateString()
                : '';
              return (
                <ListItem
                  key={app.metadata.name}
                  button
                  component="a"
                  href={`/catalog/${ns}/resource/${app.metadata.name}`}
                >
                  <ListItemText
                    primary={app.metadata.name}
                    secondary={[stack, date].filter(Boolean).join(' · ')}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      href={`/catalog/${ns}/resource/${app.metadata.name}`}
                    >
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
        <Link href="/catalog?kind=Resource" className={classes.viewAll}>
          View all in catalog →
        </Link>
      </CardContent>
    </Card>
  );
};
